// #region Global Imports
import * as React from 'react';
import cx from 'classnames';
import type { NextPage } from 'next/types';
import InfiniteScroll from 'react-infinite-scroller';
import Skeleton from 'react-loading-skeleton';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';
// #endregion Global Imports
// #region Local Imports
import type { GetServerSideProps } from 'next';
import { useQuery } from '@apollo/client';
import { format } from 'date-fns';
import { inputHelpers } from '@components/Search/functions';
import styles from '@components/Search/styles.module.sass';
import { Layout } from '@components/Layout';
import type { PostTeasersFragment } from '@components/PostTeasers';
import { PostTeasers } from '@components/PostTeasers';
import { Switch } from '@components/Switch';
import {
  addApolloState,
  initializeApollo,
  withDefaultVariables,
} from '@services/apollo';
import { CONSTANTS } from '@constants';
import { DayPickerInput } from '@components/Search/DayPickerInput';
import type { IPages } from 'types';
import type { LanguageId } from '@generated/graphql';
import { SearchQueryDocument } from '@generated/graphql';
import { renderBlockContentTranslation } from '@lib/func/renderBlockContentTranslation';
import type { FragmentType } from '@generated/fragment-masking';
import type { ImageFragment } from '@components/Paragraphs/ParagraphMedia/Image';
import { Image } from '@components/Paragraphs/ParagraphMedia/Image';
import { withSession } from '@lib/auth';
import { Spinner } from '@components/Spinner';

// #endregion Local Imports

interface IState {
  language: LanguageId;
  page: number;
  languageStr: string;
  text: string;
  issue: string;
  category: string;
  tag: string;
  author: string;
  dateMin?: string;
  dateMax?: string;
  path: string;
  translate: LanguageId;
  section: string;
  year: string;
}

// NOTE: trex-drupal #540
const EXCLUDED_CATEGORIES = [61, 75, 59, 63];

export const guessFirstName = (name: string) =>
  String(/^([\w\-]+)/.exec(name)?.[1] || '');

export const guessSecondName = (name: string) =>
  String(/\s(.+)/.exec(name)?.[1] || '');

const compareByUnicode = (
  a?: string | null | undefined,
  b?: string | null | undefined,
) => (a || '').localeCompare(b || '');

function fixSearchParams<
  T extends {
    language: string;
    year: string;
    issue: string;
  },
>(
  values: T,
): T & {
  issue: string;
  language: LanguageId;
  languageStr: string;
} {
  return {
    ...CONSTANTS.SEARCH.EMPTY_SEARCH,
    ...values,
    language: values.language as LanguageId,
    languageStr: values.language.toLowerCase(),
    issue: !/^\d\/\d+$/.exec(values.issue)
      ? values.issue && values.year && `${values.issue}/${values.year}`
      : values.issue,
  };
}

const Search: NextPage<IPages.Search> = ({ language, variables, user }) => {
  const router = useRouter();
  const { locale } = language;
  const [state, setState] = React.useState<IState>({
    ...CONSTANTS.SEARCH.EMPTY_SEARCH,
    ...variables,
  });

  React.useEffect(() => {
    setState({
      ...CONSTANTS.SEARCH.EMPTY_SEARCH,
      ...variables,
    });
  }, [router.asPath]);

  const { data, fetchMore, refetch, loading } = useQuery(SearchQueryDocument, {
    // dont mutate every time …
    variables: fixSearchParams({
      ...CONSTANTS.SEARCH.EMPTY_SEARCH,
      ...variables,
    }),
  });

  if (loading) return <Spinner />;

  const {
    termSearchArchive: _termSearchArchive,
    termSearchMainTitle: _termSearchMainTitle,
    termSearchIndexWordSearch: _termSearchIndexWordSearch,
    termSearchByTheme: _termSearchByTheme,
    termSearchByIssue: _termSearchByIssue,
    termSearchByAuthor: _termSearchByAuthor,
    termSearchTypeof: _termSearchTypeof,
    termSearchByTag: _termSearchByTag,
    termSearchDateFromTo: _termSearchDateFromTo,
    termSearchByYear: _termSearchByYear,
    termSearchCounterTitle: _termSearchCounterTitle,
    termSearchResultMessage: _termSearchResultMessage,
    issues: _issues,
    tags: _tags,
    categories: _categories,
    authors,
    author: _author,
  } = data!;

  const termSearchMainTitle = renderBlockContentTranslation(
    _termSearchMainTitle,
    'Index',
  ) as string;

  const termSearchArchive = renderBlockContentTranslation(
    _termSearchArchive,
    'Archive',
  ) as string;

  const termSearchIndexWordSearch = renderBlockContentTranslation(
    _termSearchIndexWordSearch,
    'Fulltext',
  ) as string;

  const termSearchByTheme = renderBlockContentTranslation(
    _termSearchByTheme,
    'Theme',
  ) as string;

  const termSearchByIssue = renderBlockContentTranslation(
    _termSearchByIssue,
    'Issue',
  ) as string;

  const termSearchByAuthor = renderBlockContentTranslation(
    _termSearchByAuthor,
    'Author',
  ) as string;

  const termSearchTypeof = renderBlockContentTranslation(
    _termSearchTypeof,
    'Typeof',
  ) as string;

  const termSearchByTag = renderBlockContentTranslation(
    _termSearchByTag,
    'Tag',
  ) as string;

  const termSearchDateFromTo = renderBlockContentTranslation(
    _termSearchDateFromTo,
    'From To',
  ) as string;

  const termSearchByYear = renderBlockContentTranslation(
    _termSearchByYear,
    'Year',
  ) as string;

  const termSearchCounterTitle = renderBlockContentTranslation(
    _termSearchCounterTitle,
    'Count',
  ) as string;

  const termSearchResultMessage = renderBlockContentTranslation(
    _termSearchResultMessage,
    'Results',
  ) as string;

  const dateFromTo = termSearchDateFromTo.split(' ');

  let hasMoreItems = false;
  const searchResultCount = data?.search?.count ?? 0;

  if (
    searchResultCount > 0 &&
    (data?.search?.entities?.length ?? 0) < searchResultCount
  ) {
    hasMoreItems = true;
  }

  type LabelList = (string | undefined | null)[] | undefined;

  const issues: LabelList = _issues?.entities?.map(
    (entity): string | null | undefined => {
      if (entity?.__typename === 'TaxonomyTermIssue') {
        return entity.entityTranslation?.label;
      }
      return undefined;
    },
  );

  const tags: LabelList = [
    ...new Set(
      _tags?.entities?.map((entity): string | null | undefined => {
        if (entity?.__typename === 'TaxonomyTermTags') {
          return entity.entityTranslation?.label;
        }
        return undefined;
      }),
    ),
  ];

  const categories: LabelList = [
    ...new Set(
      _categories?.entities
        ?.filter(
          (entity) =>
            !EXCLUDED_CATEGORIES.includes(
              parseInt(entity?.entityTranslation?.entityId || ''),
            ),
        )
        ?.map((entity): string | null | undefined => {
          if (entity?.__typename === 'TaxonomyTermCategory') {
            return entity.entityTranslation?.label;
          }
          return undefined;
        }),
    ),
  ];

  const issueYears = [
    ...new Set(
      issues
        ?.map((issue): string => String(issue?.split('/')[1]))
        .sort((a, b) => parseInt(b) - parseInt(a)),
    ),
  ].filter(Boolean);

  const issueNumbers = [
    ...new Set(
      issues?.map(
        (issue): string =>
          (state.year === issue?.split('/')[1] && issue.split('/')[0]) || '',
      ),
    ),
  ].filter(Boolean);

  const sortedAuthors = authors?.entities
    ?.slice()
    .filter((a: any) => a?.name)
    .sort((a: any, b: any) => {
      const res = (b?.lastName || guessSecondName(b?.name)).localeCompare(
        a?.lastName || guessSecondName(a?.name),
      );
      return res !== 0
        ? res
        : (b?.firstName || guessFirstName(b?.name)).localeCompare(
            a?.firstName || guessFirstName(a?.name),
          );
    })
    .reverse();

  const author = _author?.entities?.[0]?.value;

  return (
    <div className="Index">
      <Layout
        data={data}
        language={language}
        title={termSearchArchive}
        user={user ?? {}}
      >
        {author?.__typename === 'User' && (
          <div className={styles.Search__author_wrapper}>
            <div className={styles.Search__author_image}>
              {!isEmpty(author.name) && (
                <h2 className={cx(styles.Search__author_texts, 'h1')}>
                  {author.name}
                </h2>
              )}
              {!isEmpty(author.image?.entity) && !isEmpty(author.text) && (
                <Image
                  // @ts-expect-error
                  caption={author.text}
                  image={
                    author.image?.entity as FragmentType<typeof ImageFragment>
                  }
                />
              )}
            </div>
            <div className={styles.Search__author_root}>
              {!isEmpty(author.name) && (
                <h2 className={cx(styles.Search__author_textm, 'h1')}>
                  {author.name}
                </h2>
              )}
              {!isEmpty(author.description?.processed) && (
                <div
                  className={styles.Search__author_description}
                  dangerouslySetInnerHTML={{
                    __html: author.description?.processed ?? '',
                  }}
                />
              )}
            </div>
          </div>
        )}
        <div className={styles.Search__form}>
          <h4 className={cx(styles.Search__claim, 'h1-small')}>
            {termSearchArchive}
          </h4>
          <h1>{termSearchMainTitle}</h1>
          <form
            onSubmit={async (event): Promise<void> => {
              await inputHelpers.handleSubmit(event, state, router);
              await refetch();
            }}
          >
            <input
              className={styles.Search__text}
              onChange={(e) =>
                inputHelpers.handleInputChange('text', e, state, setState)
              }
              placeholder={termSearchIndexWordSearch}
              type="text"
              value={state.text}
            />

            <div className={styles.Search__issue}>
              <div className={styles.Search__dropdown_caption}>
                {termSearchByAuthor}:
              </div>
              <div className={styles.Search__dropdown}>
                <select
                  onChange={(e) =>
                    inputHelpers.handleInputChange(
                      'author',
                      e,
                      { ...state },
                      setState,
                    )
                  }
                  value={state.author}
                >
                  <option value="">–</option>
                  {sortedAuthors?.map((item, index: number) => {
                    return item?.__typename === 'User' ? (
                      <option key={String(index)} value={String(item.name)}>
                        {String(item.name)}
                      </option>
                    ) : null;
                  })}
                </select>
              </div>
            </div>

            <div className={styles.Search__switch}>
              <div className={styles.Search__switch_Inner}>
                <div className={styles.Search__switch_Inner2}>
                  <Switch
                    data={['thema', 'issue']}
                    label={[termSearchByTheme, termSearchByIssue]}
                    onChange={(value: string): void => {
                      return setState({
                        ...state,
                        section: value,
                      });
                    }}
                    value={state.section}
                  />
                </div>
              </div>
            </div>

            {state.section === 'thema' && (
              <div className={styles.Search__tab_thema}>
                <div className={styles.Search__category}>
                  <div className={styles.Search__dropdown_caption}>
                    {termSearchTypeof}:
                  </div>
                  <div className={styles.Search__dropdown}>
                    <select
                      onChange={(e) =>
                        inputHelpers.handleInputChange(
                          'category',
                          e,
                          { ...state },
                          setState,
                        )
                      }
                      value={state.category}
                    >
                      <option value="">–</option>
                      {categories
                        .sort((a, b) => compareByUnicode(a, b))
                        .map((category, index) => {
                          return category ? (
                            <option key={index} value={category}>
                              {category}
                            </option>
                          ) : null;
                        })}
                    </select>
                  </div>
                </div>

                <div className={styles.Search__tag}>
                  <div className={styles.Search__dropdown_caption}>
                    {termSearchByTag}:
                  </div>
                  <div className={styles.Search__dropdown}>
                    <select
                      onChange={(e) =>
                        inputHelpers.handleInputChange(
                          'tag',
                          e,
                          state,
                          setState,
                        )
                      }
                      value={state.tag}
                    >
                      <option value="">–</option>
                      {tags
                        .sort((a, b) => compareByUnicode(a, b))
                        .map((tag, index) => {
                          return tag ? (
                            <option key={index} value={tag}>
                              {tag}
                            </option>
                          ) : null;
                        })}
                    </select>
                  </div>
                </div>
                <div className={styles.Search__date}>
                  <div className={styles.Search__date_from}>
                    <div className={styles.Search__date_title}>
                      {dateFromTo[0] || 'Date'}:
                    </div>
                    <div className={styles.Search__date_subtitle}>
                      {dateFromTo[1] || 'From'}
                    </div>
                    <div className={styles.Search__date}>
                      <DayPickerInput
                        locale={locale}
                        onChange={(day) => {
                          setState({
                            ...state,
                            dateMin: day
                              ? format(day, 'yyyy-MM-dd')
                              : undefined,
                          });
                        }}
                        selected={
                          state.dateMin ===
                          CONSTANTS.SEARCH.EMPTY_SEARCH.dateMin
                            ? undefined
                            : state.dateMin
                        }
                      />
                    </div>
                  </div>

                  <div className={styles.Search__date_to}>
                    <div className={styles.Search__date_title}>&nbsp;</div>
                    <div className={styles.Search__date_subtitle}>
                      {dateFromTo[2] || 'To'}
                    </div>
                    <div className={styles.Search__date}>
                      <DayPickerInput
                        locale={locale}
                        onChange={(day) => {
                          setState({
                            ...state,
                            dateMax: day
                              ? format(day, 'yyyy-MM-dd')
                              : undefined,
                          });
                        }}
                        selected={
                          state.dateMax ===
                          CONSTANTS.SEARCH.EMPTY_SEARCH.dateMax
                            ? undefined
                            : state.dateMax
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {state.section === 'issue' && (
              <div className={styles.Search__Tab_Issue}>
                <div className={styles.Search__issue}>
                  <div className={styles.Search__dropdown_caption}>
                    {termSearchByYear}
                  </div>
                  <div className={styles.Search__dropdown}>
                    <select
                      onChange={(e) =>
                        inputHelpers.handleInputChange(
                          'year',
                          e,
                          state,
                          setState,
                        )
                      }
                      value={state.year}
                    >
                      <option value="">–</option>
                      {issueYears.map((year, index) => (
                        <option key={index} value={year}>
                          20{year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className={styles.Search__issue}>
                  <div className={styles.Search__dropdown_caption}>
                    {termSearchByIssue}
                  </div>
                  <div className={styles.Search__dropdown}>
                    <select
                      onChange={(e) =>
                        inputHelpers.handleInputChange(
                          'issue',
                          e,
                          state,
                          setState,
                        )
                      }
                      value={state.issue}
                    >
                      <option value="">–</option>
                      {issueNumbers.map((number, index) => (
                        <option key={index} value={number}>
                          {number}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div>
              <input
                className={styles.Search__submit}
                disabled={loading}
                type="submit"
                value={termSearchMainTitle}
              />
            </div>
          </form>
        </div>

        <div className={styles.Search__result}>
          <p>
            {(searchResultCount ?? 0) <= 0
              ? termSearchResultMessage
              : `${data?.search?.count ?? '-1'} ${termSearchCounterTitle}`}
          </p>
          <InfiniteScroll
            hasMore={hasMoreItems}
            loadMore={() => {
              const filteredValues = fixSearchParams({
                ...state,
                page: (state.page += 1),
              });

              return fetchMore({
                variables: {
                  ...filteredValues,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => ({
                  ...previousResult,
                  search: {
                    ...previousResult.search,
                    entities: [
                      // @ts-expect-error
                      ...previousResult.search?.entities,
                      ...fetchMoreResult.search?.entities,
                    ],
                  },
                }),
              });
            }}
            loader={
              <Skeleton
                className="SkeletonLoader__root"
                containerClassName="SkeletonLoader__wrapper"
                count={4}
                key={0}
              />
            }
            pageStart={0}
          >
            <div className={cx('PostTeaserLink__root_gray')}>
              <PostTeasers
                entityQueryResult={
                  data?.search as FragmentType<typeof PostTeasersFragment>
                }
                termIssue=""
              />
            </div>
          </InfiniteScroll>
        </div>
      </Layout>
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = withSession(
  async (context) => {
    const searchParam = context.query;

    const filteredValues: any = fixSearchParams({
      ...CONSTANTS.SEARCH.EMPTY_SEARCH,
      language: context.locale?.toUpperCase() ?? 'DE',
      ...searchParam,
    });

    const variables = withDefaultVariables(context, {
      ...filteredValues,
    });

    const apolloClient = initializeApollo(null, context.req.jwt);

    await apolloClient.query({
      query: SearchQueryDocument,
      variables,
    });

    return addApolloState(context, apolloClient, {
      props: {
        variables: withDefaultVariables(context, {
          ...CONSTANTS.SEARCH.EMPTY_SEARCH,
          language: context.locale?.toUpperCase() ?? 'DE',
          ...searchParam,
        }),
      },
    });
  },
);

export default Search;
